// Formatting helpers shared by the Quant Screener components.

export const PERIOD_LABEL = { '1y': '1y', '3y': '3y', '5y': '5y', '7y': '7y', '10y': '10y', si: 'SI' };
export const SUMMARY_LABEL = {
  latest: 'latest', mean: 'avg', median: 'median', min: 'min', max: 'max',
  pct_positive: '% > 0', pct_above_cohort_median: '% > cohort median',
};

export const metricKey = (m) =>
  `${m.statistic}|${m.period === 'custom' ? `custom:${m.start}` : (m.period || '3y')}|${m.rolling ? Number(m.rolling) : 0}|${m.rolling ? (m.summary || 'latest') : 'point'}`;

// Short column heading: "IR · 3y" / "Active return · roll 12m avg · 5y" / "Sharpe · from Mar 2025".
export function metricHeading(spec, catalogue) {
  const def = catalogue?.statistics?.find((s) => s.key === spec.statistic);
  const base = spec.label && spec.label !== def?.label ? spec.label : (def?.label || spec.statistic);
  const period = spec.period === 'custom' ? `from ${monthLabel(spec.start)}` : (PERIOD_LABEL[spec.period] || spec.period);
  if (spec.rolling) return `${base} · roll ${spec.rolling}m ${SUMMARY_LABEL[spec.summary] || spec.summary} · ${period}`;
  return `${base} · ${period}`;
}

// A value on its natural scale: pct units are decimals (0.0123 -> "1.23%").
export function fmtValue(v, unit, dp) {
  if (v === null || v === undefined || Number.isNaN(v)) return '–';
  const n = Number(v);
  if (unit === 'pct') return `${(n * 100).toFixed(dp ?? 2)}%`;
  if (unit === 'x') return `${n.toFixed(dp ?? 2)}x`;
  if (unit === 'months') return `${Math.round(n)}`;
  return n.toFixed(dp ?? 2);
}

export const fmtPct = (p) => (p === null || p === undefined ? '–' : `${Math.round(p)}`);

export const REASON_TEXT = {
  no_data: 'no returns in the window',
  min_months: 'fewer than 12 observed months',
  coverage: 'below 90% coverage of the window',
  no_benchmark: 'no benchmark return series',
  no_rf: 'risk-free series missing on the fund’s months',
  not_computable: 'not computable (e.g. zero volatility)',
  insufficient_windows: 'too few valid rolling windows',
};

export const longDate = (iso) => {
  if (!iso) return '–';
  const [y, m, d] = String(iso).split('-');
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return months[Number(m) - 1] ? `${Number(d)} ${months[Number(m) - 1]} ${y}` : iso;
};

export const monthLabel = (iso) => {
  const [y, m] = String(iso).split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months[Number(m) - 1] ? `${months[Number(m) - 1]} ${y}` : iso;
};

// Client-side ordering of the rows on screen by a column key.
export function sortRows(rows, sort) {
  const dir = sort.dir === 'asc' ? 1 : -1;
  const val = (r) => {
    if (sort.key === 'score') return r.score?.total ?? null;
    if (sort.key === 'rank') return r.score?.rank_in_cohort ?? null;
    if (sort.key.includes(':')) {
      const [k, f] = sort.key.split(':');
      return r.metrics?.[k]?.[f] ?? null;
    }
    return r[sort.key] ?? null;
  };
  return [...rows].sort((a, b) => {
    const va = val(a); const vb = val(b);
    if (va === null || va === undefined) return 1;
    if (vb === null || vb === undefined) return -1;
    if (typeof va === 'string') return va.localeCompare(vb) * dir;
    return (va - vb) * dir;
  });
}

// Converts UI thresholds to the API shape (percent inputs on pct-unit values become decimals).
export function thresholdsForApi(thresholds, metrics, catalogue) {
  return thresholds.map((t) => {
    const m = metrics.find((x) => metricKey(x) === t.metric);
    const unit = catalogue?.statistics?.find((s) => s.key === m?.statistic)?.unit;
    const value = t.field === 'value' && unit === 'pct' ? t.value / 100 : t.value;
    return { metric: t.metric, field: t.field, op: t.op, value };
  });
}

// Human-readable chips for the active universe filters (empty = whole universe).
export function describeFilters(f) {
  const out = [];
  const list = (label, arr) => { if (arr?.length) out.push(`${label}: ${arr.length > 4 ? `${arr.slice(0, 4).join(', ')} +${arr.length - 4}` : arr.join(', ')}`); };
  list('Sub asset class', f.sub_asset_classes);
  list('Asset class group', f.asset_class_groups);
  list('Style', f.styles);
  list('Tilt', f.tilts);
  list('Platform', f.platforms);
  [['held', 'Held'], ['apl', 'APL'], ['flagship', 'Flagship'], ['hedged', 'Hedged']].forEach(([k, label]) => {
    if (f[k] === true) out.push(`${label}: yes`);
    if (f[k] === false) out.push(`${label}: no`);
  });
  if (f.min_months !== '' && f.min_months !== null && f.min_months !== undefined) out.push(`History ≥ ${f.min_months}m`);
  if (f.search?.trim()) out.push(`Search: “${f.search.trim()}”`);
  return out;
}

// Error text from an axios failure, including the API's structured problem lists.
export function errorText(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || 'Request failed';
  if (typeof d === 'string') return d;
  if (d.problems) return `${d.message}: ${d.problems.join('; ')}`;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join('; ');
  return JSON.stringify(d);
}
